import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Pagination,
  Alert,
  CircularProgress,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
  Breadcrumbs,
  Link,
  Chip,
} from '@mui/material';
import { FilterList, ViewList, ViewModule } from '@mui/icons-material';
import AdvancedSearch, { SearchFilters } from '../../components/search/AdvancedSearch';
import PriceDisplay from '../../components/common/PriceDisplay';
import api from '../../services/api';
import { getImageUrl } from '../../utils/imageUtils';

interface Product {
  id: number;
  title: string;
  author?: string;
  price: number;
  discountPrice?: number;
  imageUrl?: string;
  publisher?: string;
  publicationYear?: number;
  stock: number;
  featured: boolean;
}

interface SearchResponse {
  products: Product[];
  metadata: {
    totalElements: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    query?: string;
    sortBy: string;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  availableFilters: {
    categories: Array<{ id: number; name: string; productCount: number }>;
    priceRange: { minPrice: string; maxPrice: string };
    yearRange: { minYear: number; maxYear: number };
    publishers: string[];
    languages: string[];
  };
}

const SearchResults: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [searchData, setSearchData] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentFilters, setCurrentFilters] = useState<SearchFilters>({});

  const performSearch = useCallback(async (filters: SearchFilters, page: number = 0) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && value !== null) {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              params.set(key, value.join(','));
            }
          } else {
            params.set(key, String(value));
          }
        }
      });
      
      params.set('page', String(page));
      params.set('size', '20');

      const response = await api.get(`/search?${params.toString()}`);
      setSearchData(response.data);
    } catch (error) {
      setError('Failed to perform search. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const extractFiltersFromParams = useCallback((): SearchFilters => {
    const filters: SearchFilters = {};
    
    if (searchParams.get('q')) filters.query = searchParams.get('q') || undefined;
    if (searchParams.get('title')) filters.title = searchParams.get('title') || undefined;
    if (searchParams.get('author')) filters.author = searchParams.get('author') || undefined;
    if (searchParams.get('isbn')) filters.isbn = searchParams.get('isbn') || undefined;
    if (searchParams.get('publisher')) filters.publisher = searchParams.get('publisher') || undefined;
    if (searchParams.get('language')) filters.language = searchParams.get('language') || undefined;
    if (searchParams.get('categoryIds')) {
      filters.categoryIds = searchParams.get('categoryIds')?.split(',').map(Number) || undefined;
    }
    if (searchParams.get('minPrice')) filters.minPrice = Number(searchParams.get('minPrice'));
    if (searchParams.get('maxPrice')) filters.maxPrice = Number(searchParams.get('maxPrice'));
    if (searchParams.get('minYear')) filters.minYear = Number(searchParams.get('minYear'));
    if (searchParams.get('maxYear')) filters.maxYear = Number(searchParams.get('maxYear'));
    if (searchParams.get('inStock')) filters.inStock = searchParams.get('inStock') === 'true';
    if (searchParams.get('featured')) filters.featured = searchParams.get('featured') === 'true';
    if (searchParams.get('sortBy')) filters.sortBy = searchParams.get('sortBy') || undefined;
    
    return filters;
  }, [searchParams]);

  useEffect(() => {
    const filters = extractFiltersFromParams();
    setCurrentFilters(filters);
    performSearch(filters);
  }, [extractFiltersFromParams, performSearch]);

  const handleSearch = (filters: SearchFilters) => {
    setCurrentFilters(filters);
    updateURLParams(filters, 0);
  };

  const handleClearFilters = () => {
    const clearedFilters: SearchFilters = { sortBy: 'relevance' };
    setCurrentFilters(clearedFilters);
    updateURLParams(clearedFilters, 0);
  };

  const updateURLParams = (filters: SearchFilters, page: number) => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','));
          }
        } else {
          params.set(key, String(value));
        }
      }
    });
    
    if (page > 0) {
      params.set('page', String(page));
    }

    setSearchParams(params);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    updateURLParams(currentFilters, page - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleProductClick = (productId: number) => {
    navigate(`/products/${productId}`);
  };

  const getActiveFilterChips = () => {
    const chips: Array<{ label: string; onDelete: () => void }> = [];
    
    if (currentFilters.query) {
      chips.push({
        label: `Search: "${currentFilters.query}"`,
        onDelete: () => handleSearch({ ...currentFilters, query: undefined })
      });
    }
    
    if (currentFilters.author) {
      chips.push({
        label: `Author: ${currentFilters.author}`,
        onDelete: () => handleSearch({ ...currentFilters, author: undefined })
      });
    }
    
    if (currentFilters.publisher) {
      chips.push({
        label: `Publisher: ${currentFilters.publisher}`,
        onDelete: () => handleSearch({ ...currentFilters, publisher: undefined })
      });
    }
    
    if (currentFilters.minPrice || currentFilters.maxPrice) {
      chips.push({
        label: `Price: $${currentFilters.minPrice || 0} - $${currentFilters.maxPrice || '∞'}`,
        onDelete: () => handleSearch({ ...currentFilters, minPrice: undefined, maxPrice: undefined })
      });
    }
    
    return chips;
  };

  const renderProductCard = (product: Product) => {
    if (viewMode === 'list') {
      return (
        <Card key={product.id} sx={{ mb: 2, cursor: 'pointer' }} onClick={() => handleProductClick(product.id)}>
          <Box sx={{ display: 'flex', p: 2 }}>
            <CardMedia
              component="img"
              sx={{ width: 100, height: 140, objectFit: 'contain', mr: 2 }}
              image={getImageUrl(product.imageUrl)}
              alt={product.title}
            />
            <CardContent sx={{ flex: 1, p: 0, '&:last-child': { pb: 0 } }}>
              <Typography variant="h6" gutterBottom>
                {product.title}
              </Typography>
              {product.author && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  by {product.author}
                </Typography>
              )}
              {product.publisher && (
                <Typography variant="caption" color="text.secondary">
                  {product.publisher} • {product.publicationYear}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <PriceDisplay price={product.price} discountPrice={product.discountPrice} />
                {product.stock === 0 && (
                  <Chip label="Out of Stock" color="error" size="small" sx={{ ml: 2 }} />
                )}
                {product.featured && (
                  <Chip label="Featured" color="primary" size="small" sx={{ ml: 1 }} />
                )}
              </Box>
            </CardContent>
          </Box>
        </Card>
      );
    }

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
        <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => handleProductClick(product.id)}>
          <CardMedia
            component="img"
            height="200"
            image={getImageUrl(product.imageUrl)}
            alt={product.title}
            sx={{ objectFit: 'contain' }}
          />
          <CardContent>
            <Typography variant="h6" gutterBottom noWrap>
              {product.title}
            </Typography>
            {product.author && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                by {product.author}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
              <PriceDisplay price={product.price} discountPrice={product.discountPrice} />
              {product.stock === 0 && (
                <Chip label="Out of Stock" color="error" size="small" />
              )}
            </Box>
            {product.featured && (
              <Chip label="Featured" color="primary" size="small" sx={{ mt: 1 }} />
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  if (loading && !searchData) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
          Home
        </Link>
        <Typography color="text.primary">Search Results</Typography>
      </Breadcrumbs>

      <Grid container spacing={3}>
        {/* Desktop Filters Sidebar */}
        {!isMobile && (
          <Grid item md={3}>
            <AdvancedSearch
              onSearch={handleSearch}
              onClear={handleClearFilters}
              availableFilters={searchData?.availableFilters}
              loading={loading}
            />
          </Grid>
        )}

        {/* Main Content */}
        <Grid item xs={12} md={isMobile ? 12 : 9}>
          {/* Mobile Filter Button */}
          {isMobile && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setFilterDrawerOpen(true)}
                fullWidth
              >
                Filters
              </Button>
            </Box>
          )}

          {/* Search Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h5">
              {searchData?.metadata?.query ? `Search Results for "${searchData.metadata.query}"` : 'Search Results'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={() => setViewMode('grid')}
                color={viewMode === 'grid' ? 'primary' : 'default'}
              >
                <ViewModule />
              </IconButton>
              <IconButton
                onClick={() => setViewMode('list')}
                color={viewMode === 'list' ? 'primary' : 'default'}
              >
                <ViewList />
              </IconButton>
            </Box>
          </Box>

          {/* Results Count */}
          {searchData && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {searchData.metadata.totalElements} result{searchData.metadata.totalElements !== 1 ? 's' : ''} found
              {searchData.metadata.query && ` for "${searchData.metadata.query}"`}
            </Typography>
          )}

          {/* Active Filter Chips */}
          {getActiveFilterChips().length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {getActiveFilterChips().map((chip, index) => (
                <Chip
                  key={index}
                  label={chip.label}
                  onDelete={chip.onDelete}
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          )}

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* No Results */}
          {searchData && searchData.products.length === 0 && !loading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No products found matching your search criteria. Try adjusting your filters or search terms.
            </Alert>
          )}

          {/* Products Grid/List */}
          {searchData && searchData.products.length > 0 && (
            <>
              {viewMode === 'grid' ? (
                <Grid container spacing={3}>
                  {searchData.products.map(renderProductCard)}
                </Grid>
              ) : (
                <Box>
                  {searchData.products.map(renderProductCard)}
                </Box>
              )}

              {/* Pagination */}
              {searchData.metadata.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={searchData.metadata.totalPages}
                    page={searchData.metadata.currentPage + 1}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                  />
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>

      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="bottom"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{ sx: { maxHeight: '80vh' } }}
      >
        <Box sx={{ p: 2 }}>
          <AdvancedSearch
            onSearch={(filters) => {
              handleSearch(filters);
              setFilterDrawerOpen(false);
            }}
            onClear={() => {
              handleClearFilters();
              setFilterDrawerOpen(false);
            }}
            availableFilters={searchData?.availableFilters}
            loading={loading}
          />
        </Box>
      </Drawer>
    </Container>
  );
};

export default SearchResults;